import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismLoadingBar, PrismMessage } from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import GeneralSmartInsightsDetailViewSmartInsightEntryList from 'lumen/components/general/smart-insights/detail-view/smart-insight-entry-list';
import type { SmartInsightDay } from 'lumen/models';
import {
  SMART_INSIGHTS_ARCHIVE_REASON_TRANSLATIONS,
  type SmartInsightsArchiveReason,
} from 'lumen/utils/constants/smart-insights';

interface GeneralSmartInsightsDetailViewListArchivedInsightsSignature {
  Args: {
    archivedInsights: SmartInsightDay[];
    hasError?: boolean;
    isLoading: boolean;
  };
}

export default class GeneralSmartInsightsDetailViewListArchivedInsights extends Component<GeneralSmartInsightsDetailViewListArchivedInsightsSignature> {
  @service declare intl: Services['intl'];

  archiveReasonLabel = (archivedInsight: SmartInsightDay): string => {
    const { archiveReason } = archivedInsight;

    if (!archiveReason) {
      return '';
    }

    const key =
      SMART_INSIGHTS_ARCHIVE_REASON_TRANSLATIONS[
        archiveReason as SmartInsightsArchiveReason
      ];

    return key
      ? this.intl.t(key, {
          count: archivedInsight.visibleRecommendationEntries.length,
        })
      : '';
  };

  formatDate = (date: string | null): string => {
    if (!date) {
      return '';
    }

    return dayjs(date).format('ddd, MMM D');
  };

  <template>
    {{#if @isLoading}}
      <PrismLoadingBar data-test-id="archived-insights-loading" />
    {{else if @hasError}}
      <div
        class="flex h-full items-center justify-center"
        data-test-id="archived-insights-error-state"
      >
        <PrismMessage
          @description={{t "general.error"}}
          @illustration="100x100_critical"
          @title={{t "general.error_title"}}
          class="h-fit"
        />
      </div>
    {{else if @archivedInsights.length}}
      <div
        class="flex flex-col gap-600 p-400"
        data-test-id="list-archived-insights"
      >
        {{#each @archivedInsights as |archivedInsight|}}
          <div class="flex flex-col gap-400">
            <div
              class="flex flex-col gap-200"
              data-test-id="archived-insight-header"
            >
              <h4 data-test-id="archived-insight-raised-date">
                {{t
                  "smart_insights.archive.raised_on"
                  date=(this.formatDate archivedInsight.insightCreationTime)
                }}
              </h4>
              {{#if archivedInsight.archivedAt}}
                <p
                  class="text-200--regular text-neutral-default"
                  data-test-id="archived-insight-archived-date"
                >
                  {{t
                    "smart_insights.archive.archived_on"
                    date=(this.formatDate archivedInsight.archivedAt)
                  }}
                  •
                  <span
                    data-test-id="archived-insight-reason"
                  >{{this.archiveReasonLabel archivedInsight}}</span>
                </p>
              {{/if}}
            </div>

            <GeneralSmartInsightsDetailViewSmartInsightEntryList
              @openInNewTab={{false}}
              @product="smart-insights"
              @showFeedback={{false}}
              @showSource={{false}}
              @smartInsightDay={{archivedInsight}}
            />
          </div>
        {{/each}}
      </div>
    {{else}}
      <div
        class="flex h-full items-center justify-center"
        data-test-id="archived-insights-empty-state"
      >
        <PrismMessage
          @description={{t "smart_insights.archive.empty_state_description"}}
          @illustration="100x100_list"
          @title={{t "smart_insights.archive.empty_state"}}
          class="h-fit"
        />
      </div>
    {{/if}}
  </template>
}
